<script lang="ts" setup>
import type { DropdownMenuItem } from '@nuxt/ui';
import type { BlockBase, BlockSerializedChild, BlockSerializedSlotOutlet } from '#layers/block-engine/types/block';
import { tv } from 'tailwind-variants';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { useBlockIsLocked } from '#layers/block-engine/utils/block/lock';

const { child, block, selected, hovered, slotDropTarget } = defineProps<{
	child: BlockSerializedChild | BlockSerializedSlotOutlet;
	block: BlockBase;
	selected?: boolean;
	hovered?: boolean;
	slotDropTarget?: boolean;
	ui?: Partial<typeof uiVariants.slots>;
}>();

const uiVariants = tv({
	slots: {
		border: 'pointer-events-none absolute -inset-1 rounded-lg transition-all duration-150',
	},
	variants: {
		slotDropTarget: { true: {}, false: {} },
		selected: { true: {}, false: {} },
		hovered: { true: {}, false: {} },
	},
	compoundVariants: [
		{
			slotDropTarget: true,
			class: {
				border: 'border-transparent ring-2 ring-primary duration-0',
			},
		},
		{
			slotDropTarget: false,
			selected: true,
			class: {
				border: 'border border-primary duration-0',
			},
		},
		{
			slotDropTarget: false,
			selected: false,
			hovered: true,
			class: {
				border: 'border border-primary/30 duration-0',
			},
		},
		{
			slotDropTarget: false,
			selected: false,
			hovered: false,
			class: {
				border: 'border border-transparent',
			},
		},
	],
});

const appConfig = useAppConfig();
const { t } = useI18n();

const { drag, selection, removeBlock } = useBlockEditorContext();

const isLocked = useBlockIsLocked(() => child, () => block);

// Start drag

function onDragStart(event: MouseEvent) {
	if (isLocked.value) return;

	if (!drag.dragging.value) {
		drag.startDrag({
			source: 'canvas',
			blockId: block.id,
			childId: child.id,
			mouse: {
				x: event.clientX,
				y: event.clientY,
			},
		});
	}
}

// Menu

const menuItems = computed(() => {
	const items: DropdownMenuItem[] = [];

	if (!isLocked.value) {
		items.push({
			label: t('builder.context_menu.delete_block'),
			icon: appConfig.ui.icons.trash,
			onSelect: () => {
				removeBlock(child.id);
			},
		});
	}

	return items;
});

// CSS

const uiClass = computed(() => uiVariants({ slotDropTarget, selected, hovered }));
</script>

<template>
	<Transition
		leave-active-class="transition-all duration-fast"
		leave-to-class="opacity-0"
		appear
	>
		<div
			v-if="selected && selection.selectedChildIds.value.length === 1 && !drag.dragging.value"
			class="pointer-events-none absolute -top-2 -left-1 z-10000 flex -translate-y-full items-center gap-2"
		>
			<div
				class="pointer-events-auto flex items-center overflow-hidden rounded-md text-xs text-inverted backdrop-blur-lg"
			>
				<div
					class="flex items-center gap-1 bg-primary p-1 ps-1.5"
					:class="{
						'cursor-move': !isLocked,
					}"
					@mousedown="onDragStart($event)"
				>
					<DIcon
						:name="isLocked ? 'lucide:lock' : 'lucide:grip-vertical'"
						class="size-3"
					/>

					<div class="w-max max-w-[150px] truncate font-medium">
						{{ block.name }}
					</div>
				</div>

				<DDropdownMenu
					v-if="menuItems.length > 0"
					:items="menuItems"
					size="sm"
				>
					<template #default="{ open }">
						<DButton
							icon="lucide:chevron-down"
							size="sm"
							:ui="{
								base: 'size-6 flex-1 justify-center rounded-none p-0 pe-px',
								leadingIcon: 'size-5',
							}"
							class="pointer-events-auto"
							:class="{
								'bg-primary/90 text-inverted': open,
							}"
						/>
					</template>
				</DDropdownMenu>
			</div>
		</div>
	</Transition>

	<!-- Selection border -->
	<div :class="uiClass.border({ class: ui?.border })" />
</template>
